// Section dividers on the device list (client #30).
//
// A section is not a container: it is a run of consecutive devices that share
// the same `section` label. A divider row renders wherever that label changes
// from the row above, so dividers ride along with the manual drag order rather
// than regrouping it.
//
// These rules lived inline in CustomerDetail and were mirrored — by hand — in
// the tests, which meant the tests could pass while the screen was broken.
// They live here now so both use the same code.

import type { Extinguisher } from '@/data/types';

export type Sectioned = Pick<Extinguisher, 'section'>;

const secOf = (e: Sectioned) => (e.section ?? '').trim();

/**
 * Where a new divider should land when it is added from the toolbar.
 *
 * This used to be hardcoded to row 0. Inserting at 0 re-labels the run that
 * starts there, so the first click created a section and every click after it
 * renamed that same section — the client could only ever have one.
 *
 * The first device with no section is the natural home. When everything is
 * already sectioned, the last device starts a fresh section that rows can then
 * be dragged into.
 */
export function nextSectionAnchor(devices: readonly Sectioned[]): number {
  const free = devices.findIndex(e => !secOf(e));
  return free !== -1 ? free : Math.max(0, devices.length - 1);
}

/**
 * Insert a divider at `index`, splitting the run that starts there: every
 * device from that row on that shared the old label takes the new one, and the
 * next existing divider stops the walk.
 */
export function insertSection<T extends Sectioned>(
  devices: readonly T[],
  index: number,
  name: string
): T[] {
  const out = [...devices];
  const origin = secOf(out[index] ?? {});
  for (let i = index; i < out.length; i++) {
    if (secOf(out[i]) !== origin) break;
    out[i] = { ...out[i], section: name };
  }
  return out;
}

/** Retitle every device carrying `from`, wherever they sit in the list. */
export function renameSection<T extends Sectioned>(
  devices: readonly T[],
  from: string,
  to: string
): T[] {
  return devices.map(e => (secOf(e) === from ? { ...e, section: to } : e));
}

/** Drop a divider. The devices stay exactly where they are and lose the label. */
export function removeSection<T extends Sectioned>(
  devices: readonly T[],
  name: string
): T[] {
  return devices.map(e =>
    secOf(e) === name ? { ...e, section: undefined } : e
  );
}

/**
 * Move a whole section past the neighbouring one.
 *
 * The block is spliced as a unit so the devices keep their order inside it and
 * the rest of the list is untouched. It clears the entire adjacent run in one
 * move — including an unlabelled run, which is a run like any other — rather
 * than swapping with a single row.
 *
 * Returns the list unchanged when the section is already at that end.
 */
export function moveSection<T extends Sectioned>(
  devices: readonly T[],
  name: string,
  dir: -1 | 1
): T[] {
  const arr = [...devices];
  const start = arr.findIndex(e => secOf(e) === name);
  if (start === -1) return arr;
  let end = start;
  while (end + 1 < arr.length && secOf(arr[end + 1]) === name) end++;
  if (dir === -1 && start === 0) return arr;
  if (dir === 1 && end === arr.length - 1) return arr;

  const block = arr.slice(start, end + 1);
  const rest = [...arr.slice(0, start), ...arr.slice(end + 1)];

  let at: number;
  if (dir === -1) {
    const prevSec = secOf(rest[start - 1]);
    let p = start - 1;
    while (p > 0 && secOf(rest[p - 1]) === prevSec) p--;
    at = p;
  } else {
    const nextSec = secOf(rest[start]);
    let n = start;
    while (n < rest.length && secOf(rest[n]) === nextSec) n++;
    at = n;
  }
  return [...rest.slice(0, at), ...block, ...rest.slice(at)];
}
